import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import VehicleInsuranceService from "../services/vehicleInsuranceService";
import VehicleTypeService from "../services/VehicleTypeService";
import VehicleRegistrationService from "../services/vehicleRegistrationService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import MessageDialog from "../components/MessageDialog";


export default function AddVehicleInsurance() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [editMode, setEditMode] = useState(false);

  // LIVE DATE ERROR STATE
  const [dateError, setDateError] = useState("");

  const navigate = useNavigate();
  // const messageTimer = useRef(null);
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const insuranceDate = watch("insuranceDate");
  const nextInsuranceDate = watch("nextInsuranceDate");

  
  useEffect(() => {
    if (!insuranceDate || !nextInsuranceDate) {
      setDateError("");
      return;
    }

    const ins = new Date(insuranceDate);
    const nextIns = new Date(nextInsuranceDate);

    if (nextIns <= ins) {
      setDateError("Next Insurance Date must be greater than Insurance Date");
    } else {
      setDateError("");
    }
  }, [insuranceDate, nextInsuranceDate]);
  
  const fetchVehicleTypes = async () => {
    try {
      const res = await VehicleTypeService.getActiveVehicleTypes();
      setVehicleTypes(res.data || []);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
      setVehicleTypes([]);
    }
  };

  
  const fetchVehicles = async (vehicleTypeId) => {
    if (!vehicleTypeId) return;

    try {
      const activeCompany = StorageService.activeCompany();

      if (!activeCompany?.companyId) {
        console.error("Company ID missing");
        setVehicles([]);
        return;
      }

      const res = await VehicleRegistrationService.getByVehicleTypeId(
        vehicleTypeId,
        activeCompany.companyId
      );

      const data =
        res?.data || res?.data?.data || res || [];

      setVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };

  
 useEffect(() => {
  fetchVehicleTypes();

  if (id) {
    setEditMode(true);
    setEditLoading(true);   
    setLoading(true);
    fetchVehicleInsuranceById(id);
  }
}, [id]);


  
  useEffect(() => {
    if (!editMode) {
      const today = new Date().toISOString().split("T")[0];
      setValue("insuranceDate", today);
    }
  }, [editMode, setValue]);

  const fetchVehicleInsuranceById = async (recordId) => {
    setLoading(true);
    setEditLoading(true);
    try {
      const record = await VehicleInsuranceService.getById(recordId);

      if (!record) {
        showMessage("Failed to load Vehicle Insurance details", "error");
        return;
      }

      const insuranceDate = record.insuranceDate
        ? new Date(record.insuranceDate).toISOString().split("T")[0]
        : "";

      const nextInsuranceDate = record.nextInsuranceDate
        ? new Date(record.nextInsuranceDate).toISOString().split("T")[0]
        : "";

      if (record.vehicleTypeId) {
        await fetchVehicles(record.vehicleTypeId);
      }

      reset({
        vehicleTypeId: record.vehicleTypeId || "",
        vehicleId: record.vehicleId || "",
        insuranceDate,
        nextInsuranceDate,
        status: record.status || "Active",
      });
    } catch (error) {
      console.error("Error fetching Vehicle Insurance:", error);
      showMessage("Error loading Vehicle Insurance", "error");
    } finally {
      setLoading(false);
       setEditLoading(false);
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  // SAVE / UPDATE
  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const insDate = new Date(data.insuranceDate);
      const nextInsDate = new Date(data.nextInsuranceDate);

      if (nextInsDate <= insDate) {
        showMessage("Next Insurance Date must be greater than Insurance Date", "error");
        setLoading(false);
        return;
      }

      const selectedVehicle = vehicles.find((v) => v.id == data.vehicleId);
      data.companyId = selectedVehicle?.companyId || null;
      data.updatedBy = StorageService.loginData().employeeId;

      let response;

      if (editMode && id) {
        response = await VehicleInsuranceService.update(id, data);
      } else {
        data.createdBy = StorageService.loginData().employeeId;
        data.status = "Active";
        response = await VehicleInsuranceService.create(data);
      }

      if (response?.status || response?.data) {
        showMessage(
          editMode
            ? "Vehicle Insurance updated successfully!"
            : "Vehicle Insurance added successfully!",
          "success"
        );

        setTimeout(() => {
          navigate("/cpnl/vehicleinsurance/view");
        }, 1200);
      } else {
        showMessage("Failed to save Vehicle Insurance", "error");
      }
    } catch (error) {
      console.error(error);
      showMessage(error?.response?.data?.message || "Save failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/cpnl/vehicleinsurance/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Vehicle Insurance" : "Add Vehicle Insurance"}
        secondTitle="Vehicle Management"
      />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {/* {message && (
            <div
              className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"} 
              d-flex justify-content-between align-items-center mb-3 p-2`}
            >
              <span>{message}</span>
              <button type="button" className="btn-close" onClick={closeMessage}></button>
            </div>
          )} */}
          
            <MessageDialog
              message={message}
              type={msgType}
              setMessage={setMessage}
              setMsgType={setMsgType}
            />
          

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading vehicleInsurance details...</div>
            </div>
          ) : (

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vehicle Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("vehicleTypeId", { required: "Vehicle Type is required" })}
                    onChange={(e) => fetchVehicles(e.target.value)}
                  >
                    <option value="">Select Vehicle Type</option>
                    {vehicleTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.vehicleType}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleTypeId && (
                    <small className="text-danger">{errors.vehicleTypeId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vehicle <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    {...register("vehicleId", { required: "Vehicle is required" })}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vehicleName} - {v.registrationNo}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleId && (
                    <small className="text-danger">{errors.vehicleId.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Insurance Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("insuranceDate", {
                      required: "Insurance Date is required",
                    })}
                  />
                  {errors.insuranceDate && (
                    <small className="text-danger">{errors.insuranceDate.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Next Insurance Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("nextInsuranceDate", {
                      required: "Next Insurance Date is required",
                    })}
                  />

                
                  {errors.nextInsuranceDate && (
                    <small className="text-danger">{errors.nextInsuranceDate.message}</small>
                  )}

                
                  {dateError && <small className="text-danger">{dateError}</small>}
                </div>
              </div>

              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading || dateError}
                >
                  <i className="ti ti-circle-check me-1"></i>
                  {loading ? (editMode ? "Updating..." : "Saving...") : editMode ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
